/**
 * Method group extracted from the Konva map Stimulus controller.
 * These functions intentionally use `this` so the thin Stimulus adapter can
 * keep the existing target/value/action API while the implementation lives in
 * smaller feature-oriented modules.
 */
import Konva from 'konva';
import { characterTokenData, characterTokenGeometry } from './character_token_geometry.js';
import { createCharacterTokenShape } from './character_token_shape.js';
import { areaEffectData, areaEffectGeometry } from './area_effect_geometry.js';
import { createAreaEffectShape } from './area_effect_shape.js';
import {
  CANVAS_SAVE_DEBOUNCE_MS,
  DRAW_WIDTH,
  ERASE_RADIUS,
  MAX_FOG_BRUSH_SIZE,
  MIN_FOG_BRUSH_SIZE,
  MAX_SCALE,
  MIN_SCALE,
  MIN_SHAPE_DIMENSION,
  POSITION_ZOOM_STEP,
  SHAPE_COLORS,
  SHAPE_DEFAULTS,
  SHAPE_TRANSFORMER_OPTIONS,
  TOOLBAR_DEFAULT_TOOLS,
  TOOLBAR_TOOLS,
  TOOLBAR_URL_PARAM,
  ZOOM_STEP
} from './constants.js';

export const PersistenceMethods = {
  async _refreshPlayerMap() {
    if (
      !this._mapConnected ||
      document.hidden ||
      this._playerRefresh ||
      this._pendingCanvasJson ||
      this._savingCanvasJson ||
      this._failedCanvasJson ||
      this._painting ||
      this._shapeDragging ||
      this._gestureSnapshot ||
      this._editingShapeId
    )
      return;
    this._playerRefresh = true;
    try {
      const response = await fetch(this.stateUrlValue, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) {
        this.hiddenValue = true;
        this._playerRefreshNotice = 'This map is no longer available. Open Active map from the navigation.';
        return;
      }
      const data = await response.json();
      if (
        this._pendingCanvasJson ||
        this._savingCanvasJson ||
        this._failedCanvasJson ||
        this._painting ||
        this._shapeDragging ||
        this._gestureSnapshot ||
        this._editingShapeId
      )
        return;
      this.canvasRevisionValue = data.canvas_revision;
      this.hiddenValue = data.hidden;
      for (const [key, value] of Object.entries(data.grid || {})) {
        const property = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
        this[`${property}Value`] = value;
      }
      this._playerRefreshNotice = data.hidden ? 'Map Hidden by DM' : null;
      if (!data.hidden && data.canvas !== this.canvasJsonValue) this.canvasJsonValue = data.canvas;
    } catch (error) {
      this._playerRefreshNotice = 'Map updates unavailable. Reconnecting…';
    } finally {
      this._playerRefresh = false;
      this._syncSaveStatus();
    }
  },

  _setSaveState(kind, state) {
    this._saveStates ||= {};
    this._saveStates[kind] = state;
    this._syncSaveStatus();
  },

  _syncSaveStatus() {
    const states = Object.values(this._saveStates || {});
    const failed = states.includes('failed');
    const text = this._canvasConflict
      ? 'Map changed in another session. Reload before saving.'
      : failed
        ? 'Save failed'
        : states.includes('saving')
          ? 'Saving…'
          : states.includes('dirty')
            ? 'Unsaved changes'
            : this._playerRefreshNotice || 'Saved';
    for (const target of this.saveStatusTargets || []) {
      target.textContent = text;
      target.classList.toggle('text-danger', failed);
    }
    for (const target of this.retrySaveTargets || []) target.hidden = !failed || this._canvasConflict;
    const count = this._unsavedMapEditCount?.() || 0;
    for (const target of this.unsavedNoticeTargets || []) target.hidden = this._autosaveEnabled !== false || !count;
    for (const target of this.unsavedCountTargets || [])
      target.textContent = `${count} unsaved ${count === 1 ? 'change' : 'changes'}`;
    if (this.hasMobileSaveNoticeTarget)
      this.mobileSaveNoticeTarget.hidden = !failed && !this._canvasConflict && !this._playerRefreshNotice;
    this._syncHistoryControls?.();
    this._workspaceGuard?.update(this._hasUnsavedMapChanges());
  },

  retryMapSave(event) {
    event?.preventDefault();
    if (this._failedCanvasJson) {
      this._pendingCanvasJson ||= this._failedCanvasJson;
      this._flushCanvasSave();
    }
    if (this._failedPositionJson) {
      this._pendingPositionJson ||= this._failedPositionJson;
      this._flushPositionSave();
    }
  },

  _save({ debounce = true } = {}) {
    if (this.readonlyValue || !this.hasSaveUrlValue) return;

    this._canvasBaseRevision ??= this.canvasRevisionValue;
    this._pendingCanvasJson = JSON.stringify(this._toJson());
    this._trackMapEdit?.('canvas', this._pendingCanvasJson, this.canvasJsonValue);
    this._recordHistory?.(this._pendingCanvasJson);
    // Stage rebuilds and Turbo snapshots must contain the latest edits.
    this.canvasJsonValue = this._pendingCanvasJson;
    const unchanged = this._clearSavedMapDraft?.('canvas');
    this._setSaveState('canvas', unchanged ? 'saved' : this._autosaveEnabled === false ? 'dirty' : 'saving');
    this._rememberEditorState?.();
    clearTimeout(this._canvasSaveDebounce);

    if (unchanged || this._autosaveEnabled === false) return;

    if (debounce) {
      this._canvasSaveDebounce = setTimeout(() => this._flushCanvasSave(), CANVAS_SAVE_DEBOUNCE_MS);
      return;
    }

    this._flushCanvasSave();
  },

  _flushCanvasSave() {
    if (this.readonlyValue || !this.hasSaveUrlValue) return;

    clearTimeout(this._canvasSaveDebounce);
    this._canvasSaveDebounce = null;

    // Aborting fetch does not cancel a server-side write. Finish each save
    // before sending the latest queued snapshot.
    if (this._savingCanvasJson || this._canvasConflict) return;

    const canvasJson = this._pendingCanvasJson || JSON.stringify(this._toJson());
    const editVersion = this._mapEdits?.canvas?.version;
    this._pendingCanvasJson = null;

    if (canvasJson === this._lastSavedCanvasJson && !this._failedCanvasJson) {
      this._failedCanvasJson = null;
      this._acknowledgeMapEdits?.('canvas', canvasJson, editVersion);
      this._setSaveState('canvas', 'saved');
      return;
    }

    this._savingCanvasJson = canvasJson;
    this._failedCanvasJson = null;
    this._setSaveState('canvas', 'saving');

    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    fetch(this.saveUrlValue, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
      body: JSON.stringify({
        game_map: {
          canvas_json: this.playerValue
            ? JSON.stringify({
                ...JSON.parse(canvasJson),
                shapes: JSON.parse(canvasJson).shapes.filter((shape) => this._canEditShape(shape))
              })
            : canvasJson
        },
        canvas_revision: this._canvasBaseRevision ?? this.canvasRevisionValue
      })
    })
      .then(async (response) => {
        if (response.status === 409) {
          this._canvasConflict = true;
          this._pendingCanvasJson = null;
        }
        if (!response.ok) throw new Error(`Save failed with status ${response.status}`);
        const result = await response.json();
        this.canvasRevisionValue = result.canvas_revision;
        this._canvasBaseRevision = result.canvas_revision;
        this._lastSavedCanvasJson = canvasJson;
        this._acknowledgeMapEdits?.('canvas', canvasJson, editVersion);
        this._historyRevision = result.canvas_revision;
        this.dispatch?.('canvas-saved', {
          detail: {
            saveUrl: this.saveUrlValue,
            characters: JSON.parse(canvasJson)
              .shapes.filter((shape) => shape.type === 'character' && this._canEditShape(shape))
              .map((shape) => shape.placeableId)
          }
        });
      })
      .catch((err) => {
        this._failedCanvasJson = canvasJson;
        console.error('Save failed:', err);
      })
      .finally(() => {
        this._savingCanvasJson = null;
        this._clearSavedMapDraft?.('canvas');
        if (this._pendingCanvasJson && this._autosaveEnabled !== false) this._flushCanvasSave();
        else {
          if (!this._failedCanvasJson) this._canvasBaseRevision = null;
          this._setSaveState('canvas', this._failedCanvasJson ? 'failed' : this._pendingCanvasJson ? 'dirty' : 'saved');
        }
      });
  },

  _toJson() {
    const fogData = [];
    if (this._fogLayer) {
      fogData.push({
        filled: this._fogLayer.filled,
        strokes: this._fogLayer.strokes.map((s) => {
          if (s instanceof Konva.Rect) {
            return {
              type: 'rect',
              x: s.x(),
              y: s.y(),
              width: s.width(),
              height: s.height(),
              globalCompositeOperation: s.globalCompositeOperation()
            };
          }
          return {
            type: 'line',
            strokeWidth: s.strokeWidth(),
            globalCompositeOperation: s.globalCompositeOperation(),
            points: s.points()
          };
        })
      });
    }

    const shapesData = this._shapes.map((s) => ({
      ...(s.creatorId !== undefined ? { creatorId: s.creatorId } : {}),
      ...(s.playerEditorIds !== undefined ? { playerEditorIds: s.playerEditorIds } : {}),
      id: s.id,
      name: s.name,
      color: s.color,
      type: s.type,
      ...areaEffectData(s),
      ...characterTokenData(s),
      showLabels: s.showLabels !== false,
      aboveFog: s.aboveFog !== false,
      strokeWidth: s.strokeWidth,
      fillColor: s.fillColor,
      fillOpacity: s.fillOpacity,
      x: s.konvaShape.x(),
      y: s.konvaShape.y(),
      width: s.konvaShape.width(),
      height: s.konvaShape.height()
    }));

    return { version: 2, fog: fogData, shapes: shapesData };
  },

  _restoreFromJson(data, { animate = false } = {}) {
    if (!data || data.version !== 2) return;
    this._restoreFog(data.fog || [], { animate });
    this._restoreShapes(data.shapes || [], { animate });
  },

  _restoreFog(fogData, { animate = false } = {}) {
    if (fogData.length === 0) return;
    const layerData = fogData[0];
    const entry = this._fogLayer;
    if (!entry) return;

    const signature = JSON.stringify(layerData);
    const transition = animate && this._fogReady && this._fogSignature !== signature;
    if (transition) this._fogPresentation?.beforeUpdate();
    else if (!animate) this._fogPresentation?.finish();
    this._fogSignature = signature;

    // Clear existing fog
    if (entry.baseRect) {
      entry.baseRect.destroy();
      entry.baseRect = null;
    }
    entry.strokes.forEach((s) => s.destroy());
    entry.strokes = [];
    entry.filled = false;

    // Rebuild base rect
    if (layerData.filled) {
      entry.baseRect = new Konva.Rect(this._fogRectAttrs(this._fogCoverageAttrs()));
      entry.group.add(entry.baseRect);
      entry.filled = true;
    }

    // Rebuild strokes
    (layerData.strokes || []).forEach((sd) => {
      const isErase = sd.globalCompositeOperation === 'destination-out';
      const shape =
        sd.type === 'rect'
          ? new Konva.Rect(
              isErase
                ? {
                    x: sd.x,
                    y: sd.y,
                    width: sd.width,
                    height: sd.height,
                    fill: 'black',
                    globalCompositeOperation: sd.globalCompositeOperation,
                    listening: false
                  }
                : this._fogRectAttrs({
                    x: sd.x,
                    y: sd.y,
                    width: sd.width,
                    height: sd.height,
                    globalCompositeOperation: sd.globalCompositeOperation
                  })
            )
          : new Konva.Line({
              strokeWidth: sd.strokeWidth,
              globalCompositeOperation: sd.globalCompositeOperation,
              lineCap: 'round',
              lineJoin: 'round',
              points: sd.points
            });
      if (shape instanceof Konva.Line) this._applyFogLineStroke(shape, sd.globalCompositeOperation);
      entry.group.add(shape);
      entry.strokes.push(shape);
    });

    entry.konvaLayer.batchDraw();
    if (transition) this._fogPresentation?.afterUpdate();
  },

  _restoreShapes(shapesData, { animate = false } = {}) {
    const positions = animate && this._shapeVisualPositions?.();
    this._finishShapeAnimations?.();
    const characterMove = this._characterMove;
    const characterUndo = this._lastCharacterMove;
    this._lastCharacterMove = null;
    if (!this._shapesLayerAbove) {
      this._updateShapesList();
      return;
    }

    // A remote snapshot replaces node identities. Detach controls before those
    // nodes disappear, then reapply any surviving editor selection to new nodes.
    const selectedShapeId = this.readonlyValue ? null : this._selectedShapeId;
    if (this._selectedShapeId) {
      this._finishEffectRotation({ cancel: true });
      this._deselectShape();
    }

    // Clear existing
    this._shapes.forEach((s) => {
      s.konvaShape.destroy();
      if (s.konvaLabels) s.konvaLabels.destroy();
    });
    this._shapes = [];

    if (!shapesData || shapesData.length === 0) {
      this._movementDistances?.clear();
      this._shapesLayerBelow.batchDraw();
      this._shapesLayerAbove.batchDraw();
      this._updateShapesList();
      return;
    }

    shapesData.forEach((sd) => {
      let geometry = { x: sd.x, y: sd.y, width: sd.width, height: sd.height };
      if (sd.type === 'area-effect') {
        geometry = areaEffectGeometry(
          sd,
          this._areaEffectGrid(),
          shapesData.find((shape) => shape.id === sd.parentId)
        );
        if (!geometry) return;
      }
      if (sd.type === 'character') {
        geometry = characterTokenGeometry(sd, this._areaEffectGrid());
        if (!geometry) return;
      }
      const strokeWidth = sd.strokeWidth || SHAPE_DEFAULTS.strokeWidth;
      const fillColor = sd.fillColor || SHAPE_DEFAULTS.fillColor;
      const fillOpacity = sd.type === 'character' ? 1 : (sd.fillOpacity ?? SHAPE_DEFAULTS.fillOpacity);
      const showLabels = sd.showLabels !== false;
      const aboveFog = sd.aboveFog !== false;

      const createShape = sd.type === 'character' ? createCharacterTokenShape : createAreaEffectShape;
      const rect = createShape(['area-effect', 'character'].includes(sd.type) ? sd : {}, {
        ...geometry,
        stroke: sd.color,
        strokeWidth,
        strokeScaleEnabled: false,
        fill: fillColor === 'transparent' ? 'transparent' : this._hexToRgba(fillColor, fillOpacity),
        listening: false
      });
      const targetLayer = aboveFog ? this._shapesLayerAbove : this._shapesLayerBelow;
      targetLayer.add(rect);
      const shape = {
        id: sd.id,
        creatorId: sd.creatorId,
        playerEditorIds: sd.playerEditorIds,
        name: sd.name,
        color: sd.color,
        type: sd.type,
        ...areaEffectData(sd),
        ...characterTokenData(sd),
        showLabels,
        aboveFog,
        strokeWidth,
        fillColor,
        fillOpacity,
        konvaShape: rect
      };
      this._shapes.push(shape);
      this._recreateLabels(shape);
    });

    // Update counter to avoid name collisions with "Layer N" pattern
    this._shapes.forEach((s) => {
      const match = s.name.match(/^Layer (\d+)$/);
      if (match) this._shapeCounter = Math.max(this._shapeCounter, parseInt(match[1]));
    });
    this._shapeCounter = Math.max(this._shapeCounter, this._shapes.length);

    this._rebuildShapeZOrder();
    if (selectedShapeId && this._findShapeById(selectedShapeId)) this._selectShape(selectedShapeId);
    else this._updateShapesList();
    this._restoreCharacterMovement?.(characterMove, characterUndo);
    if (animate) this._animateShapeMovement?.(positions);
    else this._movementDistances?.sync(this._shapes);
  }
};
